import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form } from 'antd';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { CustomRow } from 'app/components/commons/CustomFields/CustomFields';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { OptGroup, Option, Select } from 'app/components/fields/Select';
import { useVoucherPacks } from 'app/hooks/data/core/admin/packs/useVoucherPacks';
import type { PublishedPackListItem } from 'app/typings/core/admin/packs/publishedPackList';
import { formatHotelName, packageTitle } from 'app/utils/hotels';
import type { PackSelectionValue } from 'app/utils/packSelection';
import { buildPackSelectionValue } from 'app/utils/packSelection';

import './RestrictedPackSelect.scss';

const MAX_OPTIONS_PER_GROUP = 10;

type Props = {
  name: string;
  layout?: 'page' | 'modal';
};

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const formatPackLabel = (pack: PublishedPackListItem) =>
  pack.isDaytime
    ? packageTitle({
        dayPackage: true,
        name: pack.hotelName,
        experienceTitle: pack.name.trim(),
        stars: pack.hotelStars,
      })
    : `${pack.nameForHotel.trim()} • ${formatHotelName({
        name: pack.hotelName,
        stars: pack.hotelStars,
      })}`;

const renderPackOption = (pack: PublishedPackListItem) => {
  const value = buildPackSelectionValue(pack);
  const label = formatPackLabel(pack);

  return (
    <Option
      key={value}
      value={value}
      label={label}
      className="restricted-pack-select__option"
    >
      <div className="restricted-pack-select__option-content">
        <div className="restricted-pack-select__thumbnail">
          <PictureComponent pictureId={pack.cover?.path ?? ''} />
        </div>
        <div className="restricted-pack-select__name">{label}</div>
      </div>
    </Option>
  );
};

const limitGroup = (
  group: PublishedPackListItem[],
  selectedPack: PublishedPackListItem | undefined
) => {
  const limited = group.slice(0, MAX_OPTIONS_PER_GROUP);

  if (selectedPack && !limited.includes(selectedPack)) {
    limited.push(selectedPack);
  }

  return limited;
};

type PackSelectProps = {
  packs: PublishedPackListItem[];
  isLoading: boolean;
  value?: PackSelectionValue;
  onChange?: (value: PackSelectionValue) => void;
};

const PackSelect = ({ packs, isLoading, value, onChange }: PackSelectProps) => {
  const [search, setSearch] = useState('');

  const { nightstayPacks, daytimePacks } = useMemo(() => {
    const query = normalize(search.trim());

    const matches = packs.filter(
      (pack) =>
        query === '' ||
        normalize(
          `${pack.name} ${pack.nameForHotel} ${pack.hotelName}`
        ).includes(query)
    );

    const selectedPack = packs.find(
      (pack) => buildPackSelectionValue(pack) === value
    );

    return {
      nightstayPacks: limitGroup(
        matches.filter((pack) => !pack.isDaytime),
        selectedPack?.isDaytime === false ? selectedPack : undefined
      ),
      daytimePacks: limitGroup(
        matches.filter((pack) => pack.isDaytime),
        selectedPack?.isDaytime === true ? selectedPack : undefined
      ),
    };
  }, [packs, search, value]);

  return (
    <Select
      className="restricted-pack-select"
      showSearch
      loading={isLoading}
      value={value}
      onChange={onChange}
      onSearch={setSearch}
      onDropdownVisibleChange={(open) => {
        if (!open) {
          setSearch('');
        }
      }}
      filterOption={false}
      optionLabelProp="label"
      dropdownMatchSelectWidth={false}
      useDefaultPopupContainer
      placeholder="Search a package by name or hotel"
    >
      <OptGroup label="Nightstay">
        {nightstayPacks.map(renderPackOption)}
      </OptGroup>
      <OptGroup label="Daytime">{daytimePacks.map(renderPackOption)}</OptGroup>
    </Select>
  );
};

export const RestrictedPackSelect = ({ name, layout = 'page' }: Props) => {
  const { data: packs = [], isLoading } = useVoucherPacks();

  const renderRow = (
    label: string,
    fieldName: number,
    onRemove: () => void
  ): ReactNode => {
    const deleteIcon = (
      <DeleteOutlined
        className="restricted-pack-select__delete"
        onClick={onRemove}
      />
    );

    if (layout === 'modal') {
      return (
        <div className="restricted-pack-select__row restricted-pack-select__row--modal">
          <Form.Item
            name={fieldName}
            label={label}
            className="restricted-pack-select__item"
          >
            <PackSelect packs={packs} isLoading={isLoading} />
          </Form.Item>
          {deleteIcon}
        </div>
      );
    }

    return (
      <CustomRow label={label} name="">
        <div className="restricted-pack-select__row">
          <Form.Item name={fieldName} noStyle>
            <PackSelect packs={packs} isLoading={isLoading} />
          </Form.Item>
          {deleteIcon}
        </div>
      </CustomRow>
    );
  };

  const renderAddButton = (onAdd: () => void): ReactNode => {
    const button = (
      <Button onClick={onAdd} icon={<PlusOutlined />}>
        Add package
      </Button>
    );

    if (layout === 'modal') {
      return (
        <Form.Item className="restricted-pack-select__add restricted-pack-select__add--modal">
          {button}
        </Form.Item>
      );
    }

    return (
      <CustomRow label="" name="">
        {button}
      </CustomRow>
    );
  };

  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field, index) => (
            <div key={field.key}>
              {renderRow(`Package #${index + 1}`, field.name, () =>
                remove(field.name)
              )}
            </div>
          ))}
          {renderAddButton(() => add())}
        </>
      )}
    </Form.List>
  );
};
